"use client";

import React, { useState } from "react";
import { Callout, Button } from "@tremor/react";
import { RiAlarmWarningLine } from "react-icons/ri";
import { useRouter } from "next/navigation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button as ShadcnButton } from "@/components/ui/button";
import { AlertTriangle, Copy, Check, ExternalLink } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface AdvisorProfileBannerProps {
  hasProfile: boolean;
}

const REGISTRATION_URL = "https://intrackplus.trusttpc.com/register";

export default function AdvisorProfileBanner({ hasProfile }: AdvisorProfileBannerProps) {
  const router = useRouter();
  const { toast } = useToast();
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleFillOutProfile = () => {
    setShowDisclaimerModal(true);
  };

  const handleConfirmAndProceed = () => {
    setShowDisclaimerModal(false);
    router.push("/personal-info");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(REGISTRATION_URL);
      setCopied(true);
      toast({
        title: "Link Copied!",
        description: "Registration link has been copied to your clipboard.",
        duration: 3000,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Please manually copy the link.",
        duration: 3000,
      });
    }
  };

  if (hasProfile) {
    return (
      <div className="w-full mb-6">
        <Callout 
          title="Profile Complete" 
          color="green"
          className="w-full"
        >
          Congratulations! You&apos;ve already filled out your investor profile.
        </Callout>
      </div>
    );
  }

  return (
    <>
      <div className="w-full mb-6">
        <Callout 
          title="Complete Your Investor Profile" 
          icon={RiAlarmWarningLine} 
          color="blue"
          className="w-full"
        >
          <p className="mb-2">You haven&apos;t filled out your investor profile yet. Complete your profile to get started.</p>
          <Button size="sm" onClick={handleFillOutProfile}>
            Fill Out Investor Profile
          </Button>
        </Callout>
      </div>

      {/* Disclaimer Modal */}
      <Dialog open={showDisclaimerModal} onOpenChange={setShowDisclaimerModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <DialogTitle className="text-xl">Important: Personal Investor Profile Only</DialogTitle>
            </div>
            <DialogDescription asChild>
              <div className="space-y-4 text-left">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="font-semibold text-amber-800 mb-2">⚠️ This form is for YOUR personal investment profile only.</p>
                  <p className="text-amber-700">
                    As an advisor, this investor profile intake form is designed exclusively for advisors who also want to invest personally. 
                    <strong> Do NOT fill out this form on behalf of your clients.</strong>
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold text-blue-800 mb-2">📋 How should your clients create their profile?</p>
                  <p className="text-blue-700 mb-3">
                    If you want your clients to have an investor profile, they must:
                  </p>
                  <ol className="list-decimal list-inside text-blue-700 space-y-1 ml-2">
                    <li>Create their own InTrack account using the registration link</li>
                    <li>Log in to their personal account</li>
                    <li>Fill out their own investor intake form</li>
                  </ol>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="font-semibold text-gray-800 mb-2">🔗 Client Registration Link</p>
                  <p className="text-gray-600 text-sm mb-2">Share this link with your clients to register:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white border rounded px-3 py-2 text-sm text-gray-700 truncate">
                      {REGISTRATION_URL}
                    </code>
                    <ShadcnButton
                      variant="outline"
                      size="sm"
                      onClick={handleCopyLink}
                      className="shrink-0"
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 mr-1 text-green-600" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </ShadcnButton>
                  </div>
                </div>

                <p className="text-sm text-gray-500 italic">
                  By clicking &quot;I Understand, Continue to My Profile&quot; below, you confirm that you are filling out this form for your own personal investment, not on behalf of a client.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
            <ShadcnButton
              variant="outline"
              onClick={() => setShowDisclaimerModal(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </ShadcnButton>
            <ShadcnButton
              onClick={handleConfirmAndProceed}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
            >
              I Understand, Continue to My Profile
            </ShadcnButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

